/**
 * Dragging a message row sideways to act on it.
 *
 * # The split, and why it is where it is
 *
 * Everything that decides *what a drag means* is a pure function of four
 * numbers and two settings, and lives here. Everything that touches a pointer,
 * a transform or an element lives in `ui/MessageList.tsx`. That line is not
 * tidiness: this project's test environment is `node` with no DOM by deliberate
 * choice, so a gesture written as one piece would have been shipped with no
 * coverage at all -- and the record on interaction code here is that the gates
 * catch none of it and Amit finds all of it. {@link track} is the half that can
 * be tested, so it is the half that holds every threshold and every rule.
 *
 * # Why a swipe has to fight for the axis
 *
 * The list scrolls vertically and the platform's back gesture starts at the
 * left edge, so a horizontal drag on a row is the third claimant on the same
 * finger. {@link track} therefore refuses to commit to a direction until the
 * movement is unambiguously sideways -- past a slop distance *and* more sideways
 * than not. Until then it answers `null` and the browser keeps the gesture,
 * which is the right default: a swipe misread as a scroll is a nuisance, and a
 * scroll misread as a swipe archives someone's mail.
 *
 * The CSS half of that agreement is `touch-action: pan-y` on the row, which
 * tells the compositor that vertical panning is the browser's and horizontal is
 * ours. Without it the browser may claim the gesture on its own thread before a
 * single event arrives here.
 */

import * as composeWindow from "./composeWindow";
import type { Envelope } from "./types";
import { markFlagged, markSeen, moveToRole } from "./actions";
import { type SwipeAction, swipeLeft, swipeRight } from "./settings";

/** Which way the finger went, and therefore which binding answers. */
export type Direction = "left" | "right";

/** The icon the backdrop shows. Resolved to a component by the list. */
export type SwipeIcon = "archive" | "reply" | "star" | "mail-open" | "mail-closed";

/** How the backdrop is coloured. Each maps to a property that exists. */
export type SwipeTone = "accent" | "ok" | "warn";

/** A drag that has committed to an axis and has something to do. */
export interface Gesture {
  dir: Direction;
  action: Exclude<SwipeAction, "none">;
  /**
   * How far the row should actually be drawn, in pixels, always positive.
   *
   * Not the raw distance: past the point where letting go would fire, the row
   * keeps moving at a third of the speed of the finger. That change of pace is
   * the only signal the gesture has that it has armed, and it arrives through
   * the fingertip rather than the eye -- which matters, because the thumb doing
   * the swiping is usually on top of the thing that would otherwise show it.
   */
  offset: number;
  /** Whether letting go now runs the action. */
  armed: boolean;
}

/**
 * Before this much sideways movement, nothing is a swipe.
 *
 * Ten pixels is roughly the slop a tap already tolerates elsewhere in this file
 * tree (`HOLD_SLOP` in the list is the same order), and it is small enough that
 * a deliberate drag feels like it engages immediately.
 */
const AXIS_SLOP = 10;

/**
 * How far the row must travel before letting go fires the action.
 *
 * Proportional to the row's width so it means the same thing on a phone and on
 * a tablet, then bounded at both ends: below ~64px the gesture fires during
 * ordinary scrolling slop, and above ~128px it cannot be completed by a thumb
 * on a large screen without shifting grip.
 */
export function threshold(width: number): number {
  return Math.min(Math.max(width * 0.28, 64), 128);
}

/** Past the threshold the row follows the finger at a third speed. */
const RESISTANCE = 3;

/** And stops entirely here, so the row never leaves its own width behind. */
const OVERSHOOT = 48;

/**
 * What the drag currently means, or `null` if it does not yet mean anything.
 *
 * `null` covers three genuinely different cases and deliberately does not
 * distinguish them, because the caller does the same thing in all three: the
 * movement is still too small to read, it is more vertical than horizontal, or
 * it is sideways but that side is bound to nothing. The last is why an unbound
 * direction is *inert* rather than drawn-and-refusing -- with both sides unbound,
 * which is how this ships, the list behaves exactly as it did before the feature
 * existed.
 */
export function track(
  dx: number,
  dy: number,
  width: number,
  left: SwipeAction,
  right: SwipeAction,
): Gesture | null {
  const distance = Math.abs(dx);
  if (distance < AXIS_SLOP) return null;
  // Strictly more sideways than not. A 45-degree drag is not a swipe; it is
  // someone scrolling with their thumb, which does not travel in a straight
  // line.
  if (distance <= Math.abs(dy)) return null;

  const dir: Direction = dx < 0 ? "left" : "right";
  const action = dir === "left" ? left : right;
  if (action === "none") return null;

  const point = threshold(width);
  const offset =
    distance <= point
      ? distance
      : point + Math.min((distance - point) / RESISTANCE, OVERSHOOT);

  return { dir, action, offset, armed: distance >= point };
}

/** The current binding of both directions, read once at the start of a drag. */
export function bindings(): { left: SwipeAction; right: SwipeAction } {
  return { left: swipeLeft.value, right: swipeRight.value };
}

/** Whether either direction is bound, and so whether to listen at all. */
export function anyBound(): boolean {
  return swipeLeft.value !== "none" || swipeRight.value !== "none";
}

/**
 * What this action would do to *this* message, named as an act rather than a
 * setting.
 *
 * The two toggles are the reason this takes a row. A backdrop reading "Read or
 * unread" while sliding out from under an unread message says nothing; "Mark
 * read" is a promise, and the person is about to let go on the strength of it.
 */
export function describe(
  row: Envelope,
  action: Exclude<SwipeAction, "none">,
): { label: string; icon: SwipeIcon; tone: SwipeTone } {
  const seen = Boolean(row.email.keywords["$seen"]);
  const flagged = Boolean(row.email.keywords["$flagged"]);
  switch (action) {
    case "read":
      return seen
        ? { label: "Mark unread", icon: "mail-closed", tone: "accent" }
        : { label: "Mark read", icon: "mail-open", tone: "accent" };
    case "archive":
      return { label: "Archive", icon: "archive", tone: "ok" };
    case "reply":
      return { label: "Reply", icon: "reply", tone: "accent" };
    case "flag":
      return flagged
        ? { label: "Unflag", icon: "star", tone: "warn" }
        : { label: "Flag", icon: "star", tone: "warn" };
  }
}

/**
 * Run it.
 *
 * Every branch goes through the same functions the toolbar and the keyboard
 * use, which is what makes a swipe cost nothing to be correct: the read-only
 * account refusal, the missing-Archive-folder message, the optimistic patch and
 * its rollback, and the undo offer on an archive are all already there and are
 * not reimplemented here. A swipe is a third way to press the same button.
 *
 * It is also why an action that cannot apply is allowed to be swiped. The
 * alternative -- checking eligibility up front and refusing to engage -- makes a
 * read-only account feel like a broken gesture, where letting it through
 * produces the sentence that says which account and why.
 */
export async function perform(row: Envelope, action: Exclude<SwipeAction, "none">): Promise<void> {
  switch (action) {
    case "read":
      await markSeen([row], !row.email.keywords["$seen"]);
      return;
    case "archive":
      await moveToRole([row], "archive");
      return;
    case "flag":
      await markFlagged([row], !row.email.keywords["$flagged"]);
      return;
    case "reply":
      await composeWindow.reply(row, false);
      return;
  }
}
